/**
 * Route elevation and gradient analysis.
 *
 * Why this exists: a loaded truck does not care about distance, it cares about
 * gradient. A 7% climb sustained for three kilometres means crawling in low
 * gear, overheating and a collapsed schedule. On the way down the risk inverts
 * into brake heat, which is what actually kills trucks. And a short wall - the
 * driver's own example, 100 m of road that gains 100 m of height - is
 * impassable for anything on wheels, yet is invisible to every routing engine.
 *
 * Two scales, because both failures are real
 * ------------------------------------------
 * Gradient is measured over SEVERAL window lengths at once (~120 m, ~400 m,
 * ~1200 m) and each stretch of road keeps its worst verdict. A single window
 * cannot do this: a long window averages a 100 m wall away to nothing, and a
 * short window is pure DEM noise on open ground. Sampling resolution also
 * adapts - a short route is measured finely, and the final few kilometres of
 * ANY route get a fine pass regardless, because the brutal access ramp is
 * usually at the destination, not on the highway.
 *
 * Where the numbers come from
 * ---------------------------
 * Mapbox Directions returns no elevation, and Tilequery costs one request per
 * point. Instead we read the same free Terrain-RGB tiles the 3D terrain uses:
 * a few dozen tiles cover a long route, the service worker caches them, and
 * each decodes to a height grid we can sample as often as we like. Elevation
 * is recovered with Mapbox's documented encoding:
 *
 *     height = -10000 + ((R * 65536 + G * 256 + B) * 0.1)
 */

import type { LngLat, Route } from '../core/types';
import { MAPBOX_TOKEN } from '../core/config';
import { cumulativeDistances, distance } from '../core/geo';
import type { VehicleProfile } from '../core/settings';
import { tierForStep } from './paceModel';

/**
 * Windows the gradient is measured over, metres. Worst corroborated verdict wins.
 *
 * The shortest window is derived per-route from the DEM's actual ground
 * resolution rather than fixed, because measuring a gradient over a distance
 * finer than the terrain model can resolve produces confident nonsense - see
 * `WINDOWS_FOR` and the note on corroboration below.
 */
const LONG_WINDOWS_M = [400, 1200];

/**
 * A public road essentially never sustains more than this. Readings above it
 * are treated as terrain-model error unless a longer window agrees, because
 * they overwhelmingly come from samples landing on the hillside beside the
 * road, or from switchbacks that a 30 m DEM stacks on top of each other.
 * Measured against the real Doi Inthanon summit road, the raw signal claimed
 * 39%; the road is about 10-13%.
 */
const IMPLAUSIBLE_GRADE = 20;
/** Hard cap so a very long route can't issue hundreds of tile requests. */
const MAX_TILES = 70;
/** The last stretch of every route gets a fine-resolution pass. */
const APPROACH_M = 4000;

export type GradeSeverity = 'gentle' | 'moderate' | 'steep' | 'severe' | 'extreme' | 'impassable';

export interface GradeSegment {
  /** Metres along the route where the slope starts / ends. */
  from: number;
  to: number;
  /**
   * Average gradient across this stretch, percent, positive uphill. Always
   * consistent with `rise / (to - from)`.
   *
   * This is the span AVERAGE, not the worst instantaneous reading inside it.
   * Peak-of-short-window was tried and abandoned: on a 30 m terrain model a
   * mountain road's hairpins and cutting walls throw up 25-40% spikes that no
   * road actually has, and reporting them turned every hill road into
   * "impassable". A denoised average over 400 m+ matches reality - measured
   * against the real Doi Inthanon and Khao Yai climbs it lands at 8-9%, which
   * is what those roads are.
   */
  grade: number;
  /** Height gained (positive) or lost (negative), metres. */
  rise: number;
  severity: GradeSeverity;
  climb: boolean;
}

export interface ElevationProfile {
  /** [distanceAlong, elevation] pairs. */
  samples: Array<[number, number]>;
  minEle: number;
  maxEle: number;
  totalAscent: number;
  totalDescent: number;
  /** Slopes worth telling the driver about, worst first. */
  notable: GradeSegment[];
  steepestClimb: number;
  steepestDescent: number;
  /** True when something on this route is beyond the vehicle. */
  blocking: boolean;
}

/* -------------------------------------------------------------------------- */
/* Terrain-RGB tile access                                                     */
/* -------------------------------------------------------------------------- */

interface DemTile {
  data: ImageData;
  size: number;
}

const tileCache = new Map<string, Promise<DemTile | null>>();

function lngLatToTile(lng: number, lat: number, z: number): { x: number; y: number } {
  const n = 2 ** z;
  const latRad = (lat * Math.PI) / 180;
  return {
    x: ((lng + 180) / 360) * n,
    y: ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
  };
}

async function loadTile(z: number, x: number, y: number): Promise<DemTile | null> {
  const key = `${z}/${x}/${y}`;
  const hit = tileCache.get(key);
  if (hit) return hit;

  const promise = (async (): Promise<DemTile | null> => {
    try {
      const url = `https://api.mapbox.com/v4/mapbox.terrain-rgb/${z}/${x}/${y}.pngraw?access_token=${MAPBOX_TOKEN}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const bitmap = await createImageBitmap(await res.blob());
      const size = bitmap.width;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close?.();
      return { data: ctx.getImageData(0, 0, size, size), size };
    } catch {
      return null;
    }
  })();

  tileCache.set(key, promise);
  return promise;
}

/**
 * Bilinear read at a tile-relative position, both fractions in 0..1.
 *
 * Fractions rather than pixels on purpose: `.pngraw` terrain tiles come back
 * 256 px, and an earlier version multiplied by a hardcoded 512 before the tile
 * had loaded. Everything past the tile's midpoint then clamped to the edge
 * pixel, so half of every tile silently reported its neighbour's height - which
 * is why a hillside destination read as sea level.
 */
function readElevation(tile: DemTile, fx0: number, fy0: number): number | null {
  const px = fx0 * tile.size;
  const py = fy0 * tile.size;
  const clamp = (v: number) => Math.min(tile.size - 1, Math.max(0, v));
  const x0 = Math.floor(clamp(px));
  const y0 = Math.floor(clamp(py));
  const x1 = Math.min(tile.size - 1, x0 + 1);
  const y1 = Math.min(tile.size - 1, y0 + 1);
  const fx = clamp(px) - x0;
  const fy = clamp(py) - y0;

  const at = (x: number, y: number): number | null => {
    const i = (y * tile.size + x) * 4;
    const d = tile.data.data;
    if (d[i + 3] === 0) return null;
    return -10000 + (d[i] * 65536 + d[i + 1] * 256 + d[i + 2]) * 0.1;
  };

  const q00 = at(x0, y0);
  const q10 = at(x1, y0);
  const q01 = at(x0, y1);
  const q11 = at(x1, y1);
  if (q00 == null || q10 == null || q01 == null || q11 == null) {
    return q00 ?? q10 ?? q01 ?? q11;
  }
  return (
    q00 * (1 - fx) * (1 - fy) + q10 * fx * (1 - fy) + q01 * (1 - fx) * fy + q11 * fx * fy
  );
}

/* -------------------------------------------------------------------------- */
/* Thresholds                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Gradient thresholds, percent.
 *
 * These are the figures road authorities design to. For a laden heavy vehicle:
 * above 6% it loses speed badly and needs a low gear; above 8% many loaded
 * trucks cannot hold a useful speed at all; 10%+ is where routes get signed as
 * unsuitable for HGVs; and beyond ~15% a loaded truck simply will not make it
 * up - traction and torque both run out. Cars tolerate roughly half again as
 * much before the same thing happens, with ~25% the practical ceiling for any
 * two-wheel-drive vehicle.
 */
function thresholds(vehicle: VehicleProfile): Record<Exclude<GradeSeverity, 'gentle'>, number> {
  const heavy = vehicle.kind === 'truck' || (vehicle.weight ?? 0) >= 5;
  return heavy
    ? { moderate: 4, steep: 6, severe: 8, extreme: 10, impassable: 15 }
    : { moderate: 6, steep: 9, severe: 12, extreme: 15, impassable: 25 };
}

function classify(
  gradeAbs: number,
  limits: Record<Exclude<GradeSeverity, 'gentle'>, number>,
): GradeSeverity {
  if (gradeAbs >= limits.impassable) return 'impassable';
  if (gradeAbs >= limits.extreme) return 'extreme';
  if (gradeAbs >= limits.severe) return 'severe';
  if (gradeAbs >= limits.steep) return 'steep';
  if (gradeAbs >= limits.moderate) return 'moderate';
  return 'gentle';
}

const RANK: Record<GradeSeverity, number> = {
  gentle: 0,
  moderate: 1,
  steep: 2,
  severe: 3,
  extreme: 4,
  impassable: 5,
};

/* -------------------------------------------------------------------------- */
/* Sampling                                                                    */
/* -------------------------------------------------------------------------- */

function sampleRoute(
  route: Route,
  spacing: number,
  fromAlong: number,
  toAlong: number,
): Array<{ at: number; point: LngLat }> {
  const coords = route.geometry.coordinates;
  const cum = cumulativeDistances(coords);
  const total = cum[cum.length - 1];
  const out: Array<{ at: number; point: LngLat }> = [];

  let target = Math.max(0, fromAlong);
  const end = Math.min(total, toAlong);
  let i = 0;
  while (target <= end && i < coords.length - 1) {
    while (i < coords.length - 2 && cum[i + 1] < target) i++;
    const segLen = distance(coords[i], coords[i + 1]) || 1;
    const t = Math.min(1, Math.max(0, (target - cum[i]) / segLen));
    out.push({
      at: target,
      point: [
        coords[i][0] + (coords[i + 1][0] - coords[i][0]) * t,
        coords[i][1] + (coords[i + 1][1] - coords[i][1]) * t,
      ],
    });
    target += spacing;
  }
  return out;
}

/** Fetch elevations for a set of sampled points, one tile request per tile. */
async function elevate(
  points: Array<{ at: number; point: LngLat }>,
  zoom: number,
): Promise<Array<[number, number]>> {
  const byTile = new Map<string, Array<{ index: number; px: number; py: number }>>();
  points.forEach((p, index) => {
    const { x, y } = lngLatToTile(p.point[0], p.point[1], zoom);
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    const key = `${tx}/${ty}`;
    const list = byTile.get(key) ?? [];
    list.push({ index, px: x - tx, py: y - ty });
    byTile.set(key, list);
  });
  if (byTile.size > MAX_TILES) return [];

  const elevations = new Array<number | null>(points.length).fill(null);
  await Promise.all(
    [...byTile.entries()].map(async ([key, list]) => {
      const [tx, ty] = key.split('/').map(Number);
      const tile = await loadTile(zoom, tx, ty);
      if (!tile) return;
      for (const s of list) elevations[s.index] = readElevation(tile, s.px, s.py);
    }),
  );

  const out: Array<[number, number]> = [];
  let last: number | null = null;
  for (let i = 0; i < points.length; i++) {
    // Carry the previous reading across a gap rather than dropping the sample.
    const height: number | null = elevations[i] ?? last;
    if (height == null) continue;
    last = height;
    out.push([points[i].at, height]);
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* Sampling plan                                                               */
/* -------------------------------------------------------------------------- */

interface SampleSpan {
  from: number;
  to: number;
  spacing: number;
  zoom: number;
}

/**
 * How finely to measure each stretch of the route, chosen by road class.
 *
 * Motorways and trunk roads are engineered to gentle, consistent gradients -
 * sampling them every 40 m would burn hundreds of DEM tiles to discover
 * nothing. Small roads, sois and site access tracks are where the short brutal
 * ramps live, and those need fine sampling or they are averaged into
 * invisibility. The final approach is always measured finely regardless of
 * what the class data claims, because that is where the driveway is.
 */
function samplingPlan(route: Route): SampleSpan[] {
  const spans: SampleSpan[] = [];
  const total = route.distance;
  let along = 0;

  for (const leg of route.legs) {
    for (const step of leg.steps) {
      const tier = tierForStep(step);
      const res =
        tier === 'motorway'
          ? { spacing: 250, zoom: 12 }
          : tier === 'dual'
            ? { spacing: 100, zoom: 13 }
            : { spacing: 40, zoom: 14 };
      const from = along;
      along += step.distance;
      if (step.distance <= 0) continue;

      const prev = spans[spans.length - 1];
      if (prev && prev.zoom === res.zoom && Math.abs(prev.to - from) < 1) {
        prev.to = along; // merge consecutive stretches of the same class
      } else {
        spans.push({ from, to: along, ...res });
      }
    }
  }

  if (!spans.length) {
    return [{ from: 0, to: total, spacing: 100, zoom: 13 }];
  }

  // Always finish fine: the access road at the destination decides whether the
  // truck can actually get in, whatever class the data gave it.
  const approachFrom = Math.max(0, total - APPROACH_M);
  const trimmed = spans
    .map((s) => ({ ...s, to: Math.min(s.to, approachFrom) }))
    .filter((s) => s.to > s.from);
  trimmed.push({ from: approachFrom, to: total, spacing: 40, zoom: 14 });
  return trimmed;
}

/* -------------------------------------------------------------------------- */
/* Analysis                                                                    */
/* -------------------------------------------------------------------------- */

/** Elevation at a given distance along, by linear interpolation of samples. */
function eleAt(samples: Array<[number, number]>, at: number): number | null {
  if (!samples.length) return null;
  if (at <= samples[0][0]) return samples[0][1];
  if (at >= samples[samples.length - 1][0]) return samples[samples.length - 1][1];
  let lo = 0;
  let hi = samples.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (samples[mid][0] <= at) lo = mid;
    else hi = mid;
  }
  const [d0, e0] = samples[lo];
  const [d1, e1] = samples[hi];
  const span = d1 - d0 || 1;
  return e0 + ((e1 - e0) * (at - d0)) / span;
}

/**
 * Slide every window length across the profile and keep the worst verdict for
 * each stretch. Short windows catch walls; long windows catch drags.
 */
/**
 * Rolling median, then a light mean. Terrain-RGB carries single-pixel spikes
 * and, near roads, values taken from the cutting wall rather than the
 * carriageway; a median rejects those outright where an average would smear
 * them across the neighbouring gradients.
 */
function denoise(samples: Array<[number, number]>, radius = 2): Array<[number, number]> {
  if (samples.length < radius * 2 + 1) return samples;
  const out: Array<[number, number]> = [];
  for (let i = 0; i < samples.length; i++) {
    const lo = Math.max(0, i - radius);
    const hi = Math.min(samples.length - 1, i + radius);
    const window = samples.slice(lo, hi + 1).map((s) => s[1]).sort((a, b) => a - b);
    out.push([samples[i][0], window[Math.floor(window.length / 2)]]);
  }
  return out;
}

/** Metres of ground per DEM pixel at a given zoom and latitude. */
function demResolution(zoom: number, lat: number): number {
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;
}

function findSegments(
  samples: Array<[number, number]>,
  limits: Record<Exclude<GradeSeverity, 'gentle'>, number>,
  spacing: number,
  shortWindow: number,
): GradeSegment[] {
  const found: GradeSegment[] = [];
  const start = samples[0][0];
  const end = samples[samples.length - 1][0];

  /** Average gradient over [at, at+win], or null if unmeasurable. */
  const gradeOver = (at: number, win: number): number | null => {
    const a = eleAt(samples, at);
    const b = eleAt(samples, at + win);
    if (a == null || b == null) return null;
    return ((b - a) / win) * 100;
  };

  for (const win of [shortWindow, ...LONG_WINDOWS_M]) {
    // A window shorter than two samples is noise, not signal.
    if (win < spacing * 2) continue;
    const step = Math.max(spacing, win / 4);

    let run: GradeSegment | null = null;
    for (let at = start; at + win <= end; at += step) {
      const e0 = eleAt(samples, at);
      const e1 = eleAt(samples, at + win);
      if (e0 == null || e1 == null) continue;

      let grade = ((e1 - e0) / win) * 100;

      // Corroboration. An extreme reading over a short window is believed only
      // if a window three times longer, centred on the same stretch, is still
      // at least "steep". Genuine mountain passes pass this test; DEM spikes
      // and hairpin artefacts do not.
      if (Math.abs(grade) > IMPLAUSIBLE_GRADE) {
        const wide = win * 3;
        const corroborating = gradeOver(Math.max(start, at - win), wide);
        const holds =
          corroborating != null &&
          Math.sign(corroborating) === Math.sign(grade) &&
          Math.abs(corroborating) >= limits.steep;
        if (!holds) continue; // terrain-model artefact - say nothing
        // Believe the direction and that it is severe, but report the
        // defensible figure rather than the spike.
        grade = Math.sign(grade) * Math.min(Math.abs(grade), IMPLAUSIBLE_GRADE);
      }

      const severity = classify(Math.abs(grade), limits);
      const climb = grade > 0;

      if (severity === 'gentle') {
        if (run) found.push(run);
        run = null;
        continue;
      }
      if (run && run.climb === climb && at <= run.to) {
        run.to = at + win;
        const re0 = eleAt(samples, run.from) ?? e0;
        const re1 = eleAt(samples, run.to) ?? e1;
        run.rise = re1 - re0;
        const span = run.to - run.from || 1;
        run.grade = (run.rise / span) * 100;
        run.severity = classify(Math.abs(run.grade), limits);
      } else {
        if (run) found.push(run);
        run = { from: at, to: at + win, grade, rise: e1 - e0, severity, climb };
      }
    }
    if (run) found.push(run);
  }

  // Keep the WORST findings rather than merging overlaps into an average.
  //
  // Merging used to fold a short brutal ramp into the long gentle drag that
  // contains it, and re-derive the gradient from the combined span - which is
  // exactly how a real destination that the driver refused to attempt came out
  // as a mild 9.8%, when its final 240 m is over 20%. Sorting by severity and
  // taking greedily, skipping only near-duplicates, preserves both the
  // sustained climb and the wall at the top of it.
  found.sort((a, b) => Math.abs(b.grade) - Math.abs(a.grade));
  const kept: GradeSegment[] = [];
  for (const s of found) {
    if (Math.abs(s.grade) > IMPLAUSIBLE_GRADE) continue;
    const span = s.to - s.from;
    const duplicate = kept.some((k) => {
      if (k.climb !== s.climb) return false;
      const overlap = Math.min(k.to, s.to) - Math.max(k.from, s.from);
      if (overlap <= 0) return false;
      // Only discard when it covers essentially the same stretch AND is not
      // materially steeper - a steeper reading over a shorter run is new
      // information, not a duplicate.
      return overlap > span * 0.75 && Math.abs(s.grade) < Math.abs(k.grade) * 1.15;
    });
    if (!duplicate) kept.push({ ...s, grade: Math.round(s.grade * 10) / 10 });
  }
  return kept.sort((a, b) => a.from - b.from);
}

/**
 * Fetch the elevation profile for a route and pick out the slopes that matter.
 * Returns null when terrain data can't be reached - callers treat that as
 * "unknown", never as "flat".
 */
export async function analyseElevation(
  route: Route,
  vehicle: VehicleProfile,
): Promise<ElevationProfile | null> {
  const plan = samplingPlan(route);

  // Sample each stretch at the resolution its road class warrants, then group
  // by DEM zoom so tile fetches still batch.
  const byZoom = new Map<number, Array<{ at: number; point: LngLat }>>();
  for (const span of plan) {
    const pts = sampleRoute(route, span.spacing, span.from, span.to);
    const list = byZoom.get(span.zoom) ?? [];
    list.push(...pts);
    byZoom.set(span.zoom, list);
  }

  const groups = await Promise.all(
    [...byZoom.entries()].map(async ([zoom, pts]) => {
      const got = await elevate(pts, zoom);
      // Too many tiles at this resolution: back off one zoom rather than
      // losing the stretch entirely.
      if (!got.length && pts.length) return elevate(pts, Math.max(11, zoom - 1));
      return got;
    }),
  );

  const raw = groups
    .flat()
    .sort((a, b) => a[0] - b[0])
    .filter((s, i, arr) => i === 0 || s[0] - arr[i - 1][0] > 1);
  if (raw.length < 3) return null;

  const samples = denoise(raw);
  const finestSpacing = Math.min(...plan.map((p) => p.spacing));

  // The finest gradient we can honestly measure is bounded by the terrain
  // model, not by how often we sampled it: below ~4 DEM pixels the reading is
  // interpolation artefact. At z14 that is ~40 m, at z12 ~150 m.
  const midLat = route.geometry.coordinates[Math.floor(route.geometry.coordinates.length / 2)][1];
  const finestZoom = Math.max(...plan.map((p) => p.zoom));
  const shortWindow = Math.max(120, Math.round(demResolution(finestZoom, midLat) * 4));

  let minEle = Infinity;
  let maxEle = -Infinity;
  let totalAscent = 0;
  let totalDescent = 0;
  for (let i = 0; i < samples.length; i++) {
    const e = samples[i][1];
    if (e < minEle) minEle = e;
    if (e > maxEle) maxEle = e;
    if (i > 0) {
      const d = e - samples[i - 1][1];
      // 2 m floor: below that it is terrain-model noise, not climbing.
      if (d > 2) totalAscent += d;
      else if (d < -2) totalDescent += -d;
    }
  }

  const heavy = vehicle.kind === 'truck' || (vehicle.weight ?? 0) >= 5;
  const limits = thresholds(vehicle);
  const segments = findSegments(samples, limits, finestSpacing, shortWindow);

  // Report a slope when it is sustained OR when it is severe at any length -
  // the short wall is exactly the case that must not be filtered out.
  const notable = segments
    .filter((s) => s.to - s.from >= 300 || RANK[s.severity] >= RANK.severe)
    .sort((a, b) => RANK[b.severity] - RANK[a.severity] || Math.abs(b.grade) - Math.abs(a.grade));

  const climbs = notable.filter((s) => s.climb);
  const drops = notable.filter((s) => !s.climb);

  return {
    samples,
    minEle: Math.round(minEle),
    maxEle: Math.round(maxEle),
    totalAscent: Math.round(totalAscent),
    totalDescent: Math.round(totalDescent),
    notable: notable.slice(0, 6),
    steepestClimb: climbs.length ? Math.round(climbs[0].grade * 10) / 10 : 0,
    steepestDescent: drops.length ? Math.round(drops[0].grade * 10) / 10 : 0,
    // Calibrated against two real destinations on Koh Samui that this fleet
    // actually attempted: one at a sustained 12.2% was refused with the loaded
    // truck, one at 7.2% was managed but described as barely doable. So for a
    // heavy vehicle the "do not commit to this" line sits at `extreme` (10%+),
    // not at the theoretical traction limit. A car keeps the higher bar.
    blocking: heavy
      ? notable.some((s) => RANK[s.severity] >= RANK.extreme)
      : notable.some((s) => s.severity === 'impassable'),
  };
}

/** True when this profile deserves a warning before setting off. */
export function isDemanding(profile: ElevationProfile | null): boolean {
  if (!profile) return false;
  return profile.notable.some((s) => RANK[s.severity] >= RANK.severe);
}

/** The single worst slope, for a one-line summary. */
export function worstSlope(profile: ElevationProfile | null): GradeSegment | null {
  return profile?.notable[0] ?? null;
}
